import { createClient } from '@/lib/supabase/client';
import type { Customer } from '@/lib/database';
import { newId, orThrow, DEFAULT_PAGE_SIZE } from './useEntityList';

type CustomerRow = Omit<Customer, 'last_purchase_date'> & { last_purchase_date: string | null };

function toCustomer(row: CustomerRow): Customer {
  return { ...row, last_purchase_date: row.last_purchase_date ?? '' };
}

export async function listCustomers(): Promise<Customer[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .order('created_at', { ascending: false });
  return (orThrow(data, error) as CustomerRow[]).map(toCustomer);
}

export async function createCustomer(
  input: Omit<
    Customer,
    'id' | 'created_at' | 'total_purchases_usd' | 'outstanding_balance_usd' | 'last_purchase_date'
  > &
    Partial<
      Pick<Customer, 'total_purchases_usd' | 'outstanding_balance_usd' | 'last_purchase_date'>
    >
): Promise<Customer> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('customers')
    .insert({
      ...input,
      total_purchases_usd: input.total_purchases_usd ?? 0,
      outstanding_balance_usd: input.outstanding_balance_usd ?? 0,
      last_purchase_date: input.last_purchase_date || null,
      id: newId('cust'),
      created_at: new Date().toISOString(),
    })
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  return toCustomer(orThrow(data, error) as CustomerRow);
}

export async function updateCustomer(
  id: string,
  patch: Partial<Omit<Customer, 'id' | 'created_at'>>
): Promise<Customer> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('customers')
    .update(patch)
    .eq('id', id)
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  return toCustomer(orThrow(data, error) as CustomerRow);
}
